<template>
    <q-card
        class="full-width q-mb-sm"
        :class="$q.screen.gt.xs ? 'q-pa-sm' : ''"
        bordered
        flat
    >
        <q-card-section
            :horizontal="$q.screen.gt.xs ? true : false"
            :class="$q.screen.gt.xs ? '' : 'q-pb-none'"
        >
            <div class="full-width justify-around">
                <!-- Fields -->
                <div>
                    <div
                        class="q-mb-md"
                        :class="$q.screen.gt.sm ? 'text-h6' : 'text-body1'"
                    >
                        <q-icon
                            class="q-ml-xs"
                            name="far fa-user"
                            size="sm"
                        ></q-icon>
                        <span class="q-mx-sm">{{ name }}</span>
                    </div>
                </div>
                <div>
                    <div class="q-mb-md text-grey-10 recipients-card--field">
                        <q-icon
                            class="q-ml-xs"
                            name="far fa-envelope-open-text"
                            size="sm"
                        ></q-icon>
                        <span class="q-mx-sm">{{ recipientEmail }}</span>
                    </div>
                </div>
                <div v-if="recipientPhoneNumber && !smsDisabled">
                    <div class="q-mb-md text-grey-10 recipients-card--field">
                        <q-icon
                            class="q-ml-xs"
                            name="far fa-phone-alt"
                            size="sm"
                        ></q-icon>
                        <span class="q-mx-sm">{{ recipientPhoneNumber }}</span>
                    </div>
                </div>
            </div>
            <div class="full-width justify-around">
                <!-- Fields -->
                <div>
                    <div
                        class="q-mb-md"
                        :class="$q.screen.gt.sm ? 'text-h6' : 'text-body1'"
                    >
                        <q-icon
                            class="q-ml-xs"
                            name="far fa-exclamation-circle"
                            size="sm"
                        >
                            <q-tooltip>
                                {{ $t('Selected options') }}
                            </q-tooltip>
                        </q-icon>
                        <span class="q-mx-sm">{{
                            'Receive notifications'
                        }}</span>
                    </div>
                </div>
                <div>
                    <div
                        class="recipients-card--field"
                        :class="$q.screen.gt.sx ? 'q-mb-md' : 'q-mb-none'"
                    >
                        <q-checkbox
                            dense
                            :disable="!isEdit"
                            v-model="emailToggle"
                            label="Email"
                            size="lg"
                            class="text-h33 q-px-sm text-black"
                            checked-icon="check"
                            color="accent"
                            unchecked-icon="clear"
                        />
                    </div>
                </div>
                <div v-if="recipientPhoneNumber && !smsDisabled">
                    <q-checkbox
                        dense
                        :disable="!isEdit"
                        v-model="smsToggle"
                        label="SMS"
                        size="lg"
                        class="text-h33 q-px-sm text-black"
                        checked-icon="check"
                        color="green"
                        unchecked-icon="clear"
                    />
                </div>
            </div>
            <!-- Actions -->
            <q-separator vertical></q-separator>
            <q-card-actions
                class="col-auto justify-around q-pl-xs q-pr-none"
                :align="$q.screen.gt.sx ? 'right' : 'center'"
                vertical
            >
                <q-card-actions
                    v-if="isSelectable"
                    class="col-auto justify-around q-pl-xs q-pr-none"
                    align="right"
                    vertical
                >
                    <q-icon
                        :name="
                            isSelected ? 'fas fa-check-square' : 'fal fa-square'
                        "
                        color="primary"
                        size="md"
                    >
                    </q-icon>
                </q-card-actions>
                <q-card-actions
                    v-else
                    class="col-auto justify-around q-pl-xs q-py-none q-pr-none"
                    align="right"
                    vertical
                >
                    <q-separator vertical></q-separator>
                    <q-btn
                        v-if="isEdit"
                        @click="save"
                        class="q-pa-sm q-mb-sm"
                        icon="fas fa-check"
                        color="positive"
                        text-color="white"
                        size="sm"
                    >
                        <q-tooltip>
                            {{ $t('Save') }}
                        </q-tooltip>
                    </q-btn>
                    <q-btn
                        v-else-if="!isEdit && !smsDisabled"
                        @click="$emit('edit')"
                        :disable="!canEdit"
                        :color="!canEdit ? 'grey' : 'accent'"
                        class="q-pa-sm card__button--edit"
                        icon="fas fa-edit"
                        size="md"
                        flat
                    />
                    <q-btn
                        v-if="isEdit"
                        @click="cancel"
                        class="q-pa-sm"
                        icon="fas fa-times"
                        color="negative"
                        size="sm"
                        outline
                    >
                        <q-tooltip>
                            {{ $t('Cancel') }}
                        </q-tooltip>
                    </q-btn>
                    <q-btn
                        v-else
                        @click="$emit('delete')"
                        :disable="!canDelete"
                        :color="!canDelete ? 'grey' : 'red-6'"
                        class="q-pa-sm card__button--delete"
                        icon="fas fa-trash"
                        size="md"
                        flat
                    />
                </q-card-actions>
            </q-card-actions>
        </q-card-section>
    </q-card>
</template>

<script>
export default {
    name: 'RecipientCard',
    emits: ['save', 'cancel', 'edit', 'delete'],
    /**
     * group fields.
     */
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        },
        assetId: {
            type: Number,
            required: true
        },
        userId: {
            type: Number,
            required: true
        },
        /**
         * Recipient props.
         */
        name: {
            type: String,
            required: true
        },
        recipientEmail: {
            type: String,
            required: true
        },
        receiveEmail: {
            type: Boolean,
            required: false
        },
        recipientPhoneNumber: {
            type: String,
            required: false
        },
        receiveSms: {
            type: Boolean,
            required: false
        },
        isEdit: {
            type: Boolean,
            required: true,
            default: false
        },
        /**
         * Card options.
         */
        // is the card currently selected?
        isSelected: {
            type: Boolean,
            required: false,
            default: false
        },
        // should cards be selectable?
        isSelectable: {
            type: Boolean,
            required: false,
            default: false
        },
        // permissions
        canEdit: {
            type: Boolean,
            required: false,
            default: false
        },
        canDelete: {
            type: Boolean,
            required: false,
            default: false
        },
        smsDisabled: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            emailToggle: this.receiveEmail,
            smsToggle: this.receiveSms
        };
    },
    watch: {
        receiveEmail: {
            handler() {
                this.emailToggle = this.receiveEmail;
            }
        },
        receiveSms: {
            handler() {
                this.smsToggle = this.receiveSms;
            }
        },
        emailToggle: {
            handler() {
                this.validateSelection(this.emailToggle, this.smsToggle);
            }
        },
        smsToggle: {
            handler() {
                this.validateSelection(this.emailToggle, this.smsToggle);
            }
        }
    },
    methods: {
        /**
         * Handle save when editing a card inline.
         */
        save() {
            const payload = {
                user: this.userId,
                asset: this.assetId,
                email: this.emailToggle,
                sms: this.smsDisabled ? false : this.smsToggle
            };
            this.$emit('save', payload);
        },
        /**
         * Handle save when editing a card inline.
         */
        cancel() {
            this.resetAlertToggles();
            this.$emit('cancel');
        },
        resetAlertToggles() {
            this.emailToggle = this.receiveEmail;
            this.smsToggle = this.receiveSms;
        },
        /**
         * Handle validation of email and sms toggles
         * If user sets both toggles to false, an error
         * message is shown and the toggles are reset to
         * the initial state
         * @param {Boolean} email - Email toggle state.
         * @param {Boolean} sms - SMS toggle state.
         */
        validateSelection(email, sms) {
            if (!email && !sms) {
                this.$notifyError(
                    this.$t(
                        'At least one alert option out of SMS or email needs to be selected. Please delete the recipient if you do not want any alerts.'
                    )
                );
                this.resetAlertToggles();
            }
        }
    }
};
</script>

<style lang="scss">
.recipients-card--field {
    font-size: 17px;
}

.recipients-card--input__lte_xs {
    padding-right: 10px;
    width: 100%;
}

.recipients-card--input__gt_xs {
    width: 250px;
}

.recipients-card__description {
    color: $grey-7;
}

.recipients-card--selectable {
    cursor: pointer;
}

.recipients-card--selected {
    background-color: $grey-3;
}

.target-downtime {
    display: inline-block;
}
</style>
